import api from '../services/api';
import { getMessagingIfSupported, isFirebaseConfigured, loadAuth, VAPID_KEY } from '../services/firebase';
import { isStandalone } from './pwa';

const STORAGE_KEY = 'pushToken';

export type PushStatus =
  | 'unsupported' // navegador sem suporte a push (ou Firebase não configurado)
  | 'needs-install' // iPhone/iPad: push só funciona com o app instalado na tela de início
  | 'default' // ainda não pediu permissão
  | 'granted'
  | 'denied';

const isIOS = () => {
  const ua = window.navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) || (ua.includes('Macintosh') && navigator.maxTouchPoints > 1);
};

export const getPushStatus = async (): Promise<PushStatus> => {
  if (!isFirebaseConfigured || !VAPID_KEY) return 'unsupported';
  if (isIOS() && !isStandalone()) return 'needs-install';
  // O service worker só é registrado em produção (ver utils/pwa.ts)
  if (process.env.NODE_ENV !== 'production') return 'unsupported';
  if (!('Notification' in window) || !('serviceWorker' in navigator)) return 'unsupported';
  if (!(await getMessagingIfSupported())) return 'unsupported';
  return Notification.permission as PushStatus;
};

const getStoredToken = () => {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
};

/**
 * Obtém o token FCM deste dispositivo e registra na API.
 * Só pede permissão se `askPermission` (precisa ser chamado a partir de um clique).
 */
export const registerDevicePush = async (askPermission: boolean): Promise<PushStatus> => {
  // O pedido de permissão vem antes de qualquer carregamento assíncrono: o Safari
  // só aceita requestPermission enquanto o clique do usuário ainda está "ativo".
  const permission = askPermission ? await Notification.requestPermission() : Notification.permission;
  if (permission !== 'granted') return permission as PushStatus;

  const firebaseMessaging = await getMessagingIfSupported();
  if (!firebaseMessaging) return 'unsupported';
  const { messaging, getToken } = firebaseMessaging;

  const registration = await navigator.serviceWorker.ready;
  const token = await getToken(messaging, { vapidKey: VAPID_KEY, serviceWorkerRegistration: registration });
  if (!token) return 'unsupported';

  await api.post('/notifications/push-tokens', { token });
  try {
    localStorage.setItem(STORAGE_KEY, token);
  } catch {
    // sem storage: o token é registrado de novo no próximo acesso
  }
  return 'granted';
};

/**
 * Remove este dispositivo das notificações (logout).
 * Recebe o JWT porque o logout já limpou o localStorage quando a requisição sai.
 */
export const unregisterDevicePush = async (authToken: string | null) => {
  const token = getStoredToken();
  if (!token) return;

  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignora
  }

  try {
    if (authToken) {
      await api.delete('/notifications/push-tokens', {
        data: { token },
        headers: { Authorization: `Bearer ${authToken}` },
      });
    }
    const firebaseMessaging = await getMessagingIfSupported();
    if (firebaseMessaging) await firebaseMessaging.deleteToken(firebaseMessaging.messaging);
  } catch (error) {
    console.error('Erro ao remover dispositivo das notificações:', error);
  }
};

/**
 * Logout: remove o push deste dispositivo e encerra a sessão do Firebase.
 */
export const teardownRealtime = async (authToken: string | null) => {
  if (!isFirebaseConfigured) return;
  await unregisterDevicePush(authToken);
  try {
    const { auth, signOut } = await loadAuth();
    await signOut(auth);
  } catch (error) {
    console.error('Erro ao encerrar sessão do Firebase:', error);
  }
};
